import { AxiosInstance } from 'axios';
import * as blake from 'blakejs';
import Emittery from 'emittery';
import { v7 as uuidv7 } from 'uuid';
import { TokenFunctions } from '../stores/buzzebeess-app.store';
import { ProfileImageSource } from '../types';

const eventEmitter = new Emittery();

function defaultCorelationIdGenerator(): string {
  return uuidv7();
}

function isJWT(token: string): boolean {
  return typeof token === 'string' && token.split('.').length === 3;
}

function addDefaultHeaderInterceptor(
  axiosInstance: AxiosInstance,
  appId: string,
  subscriptionKey: string,
  tokenFunctions: TokenFunctions,
  apiVersion: string,
  corelationIdGenerator: () => string = defaultCorelationIdGenerator
) {
  axiosInstance.interceptors.request.use(
    async (config) => {
      config.headers.set('App-Id', appId);

      if (subscriptionKey) {
        config.headers.set('Subscription-Key', subscriptionKey);
      }

      if (apiVersion) {
        config.headers.set('Api-Version', apiVersion);
      }

      config.headers.set('X-Corelation-Id', corelationIdGenerator());
      const token = await tokenFunctions.getToken();

      if (token) {
        if (isJWT(token)) {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers.set('Authorization', `token ${token}`);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  axiosInstance.interceptors.response.use(
    (response) => {
      const responseData = response.data;
      let responseModel: any;
      let responseError: { code: string; id: string; message: string } | undefined;

      const hasSuccessField = 'Success' in responseData || 'success' in responseData;

      if (hasSuccessField) {
        // New style response
        if (responseData.Success === false || responseData.success === false) {
          responseError = {
            code: String(responseData.Code || responseData.code || ''),
            id: String(responseData.Code || responseData.code || ''),
            message: String(responseData.Message || responseData.message || ''),
          };
        }
        responseModel = responseData.Data || responseData.data;
      } else {
        // Old style response
        if (responseData.error) {
          responseError = {
            code: String(responseData.error.code),
            id: String(responseData.error.id),
            message: String(responseData.error.message),
          };
        } else {
          responseModel = responseData;
        }
      }

      // Safely access buzzebees
      const buzzebeesObj = responseModel?.buzzebees;
      if (buzzebeesObj) {
        if (typeof buzzebeesObj.points === 'number') {
          eventEmitter.emit('get_points', buzzebeesObj);
        }
        if (buzzebeesObj.badges) {
          eventEmitter.emit('get_badges', buzzebeesObj.badges);
        }
      }

      if (responseError) {
        const { id, code } = responseError;
        if (code === '1905' || code === '2076' || id === '1905' || id === '2076') {
          eventEmitter.emit('session_expired', responseError);
        }
      }

      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

function generateShortLivedId(): string {
  return Math.random().toString(36).substring(2, 15);
}

function avatarUrl(baseUrl: string, appId: string, userId: string, token: string | null) {
  const url =
    `${baseUrl}/profile/${userId}/picture?timestamp=${Date.now()}&type=large` +
    `${token === null ? '' : `&token=${token}&device_app_id=${appId}`}`;
  console.log(url);
  return url;
}

function largeImage(url: string) {
  if (!url) return '';
  const result = url.replace('?', '-large?');
  return result;
}

async function fetchImage(axiosClient: AxiosInstance, imageUrl: string) {
  try {
    const response = await axiosClient.get(imageUrl, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    console.error('Error fetching image:', error);
  }
}

function createInterfaceWebsiteUrl(
  url: string,
  token?: string,
  returnUrl: string = '',
  params?: { [key: string]: any }
): string {
  if (token) {
    params = {
      token: createInterfaceToken(token),
      return_url: returnUrl,
      ...params,
    };
  }
  const searchParams = new URLSearchParams(params).toString();
  const result = url.includes('?') ? `${url}&${searchParams}` : `${url}?${searchParams}`;
  return result;
}

function createInterfaceToken(token: string): string {
  if (token.length <= 5) {
    return token;
  }

  const firstChar = token.substring(0, 1);
  const last5Chars = token.substring(token.length - 5);
  const remainingChars = token.substring(1, token.length - 5);

  return firstChar === '.' ? `${last5Chars}${remainingChars}` : token;
}

function createCartUrl(cartUrl: string, appName: string, accessKey: string, params?: { [key: string]: any }) {
  const newUrl = cartUrl.endsWith('/') ? `${cartUrl}landing/${appName}` : `${cartUrl}/landing/${appName}`;
  const newParams = {
    access_key: accessKey,
    appname_config: appName,
    ...params,
  };
  const searchParams = new URLSearchParams(newParams).toString();
  return searchParams ? `${newUrl}?${searchParams}` : newUrl;
}

function generateBlake2bSignatureHex(data: string, key: string, encoder: TextEncoder): string {
  const keyBytes = encoder.encode(key);
  return blake.blake2bHex(data, keyBytes, 32);
}

function getOtpSignatureHeaders({
  appId,
  contactNumber,
  channel,
  encoder,
  now = new Date(),
}: {
  appId: string;
  contactNumber: string;
  channel: string;
  encoder: TextEncoder;
  now?: Date;
}) {
  const dateTime = now.toISOString().split('.')[0] + 'Z'; // UTC, no ms
  const timeStamp = Math.floor(now.getTime() / 1000);
  const data = `${appId}|${contactNumber}|${channel}|${dateTime}`;
  const signature = generateBlake2bSignatureHex(data, appId, encoder);
  return {
    'OTP-Signature': signature,
    Timestamp: timeStamp,
  };
}

function addBlake2OtpSignatureInterceptor(
  axiosInstance: AxiosInstance,
  paths: string[],
  encoder: TextEncoder,
  getCurrentDateTime: () => Date = () => new Date()
) {
  axiosInstance.interceptors.request.use((config) => {
    // Only apply to matching paths
    if (paths.some((path) => config.url && config.url.includes(path))) {
      // Try to get appId, contactNumber, channel from query or data
      const getParam = (key: string) => {
        // Prefer query params
        if (config.params && config.params[key]) return config.params[key];
        // Fallback to data (for POST/PUT)
        if (config.data && typeof config.data === 'object' && config.data[key]) return config.data[key];
        return undefined;
      };
      const appId = getParam('app_id');
      const contactNumber = getParam('contact_number');
      const channel = getParam('channel');
      if (appId && contactNumber && channel) {
        const now = getCurrentDateTime();
        const headers = getOtpSignatureHeaders({ appId, contactNumber, channel, now, encoder });
        config.headers.set('OTP-Signature', headers['OTP-Signature']);
        config.headers.set('Timestamp', headers['Timestamp']);
      }
    }
    return config;
  });
}

const getProfileImageSource = (
  baseUrl: string,
  appId: string,
  subscriptionKey: string,
  apiVersion: string,
  token: string,
  corelationIdGenerator: () => string = defaultCorelationIdGenerator,
  timestamp: number = Date.now(),
  type: string = 'large'
): ProfileImageSource => {
  const uri = `${baseUrl}/profile/picture?timestamp=${timestamp}&type=${type}`;
  
  const headers: { [key: string]: string } = {
    'Api-Version': apiVersion,
    'Subscription-Key': subscriptionKey,
    'App-Id': appId,
    'X-Corelation-Id': corelationIdGenerator(),
  };

  if (isJWT(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    headers['Authorization'] = `token ${token}`;
  }

  return {
    uri,
    headers,
  };
};

export {
  addBlake2OtpSignatureInterceptor, // Export the new interceptor
  addDefaultHeaderInterceptor,
  avatarUrl,
  createCartUrl,
  createInterfaceToken,
  eventEmitter,
  fetchImage,
  generateBlake2bSignatureHex, // Export the new function
  generateShortLivedId,
  getOtpSignatureHeaders,
  createInterfaceWebsiteUrl as interfaceWebsite,
  largeImage,
  getProfileImageSource,
};
